import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {required} from '../../../../../../../shared/utils';
import {EditPermissions, NodeBuilder,} from '../../../../../../../shared/models';
import {getEditTypes} from '../../../delegatorUtils';
import isEmpty from 'lodash/isEmpty';
import {newStepValue} from "../../../../../services/flowBuilder/utils/constants";
import {ActiveFormService} from '../../../../../services/active-form.service';
import {DisplayTypeModalData} from "../../templates/display-type-dialog/displayTypeUtils";
import { ClickatellExpansionPanelComponent } from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { CMSComponent } from '../../../../../../../shared/components/cms/cms.component';
import { DisplayTypeDialogComponent } from '../../templates/display-type-dialog/display-type-dialog.component';
import { NgTemplateOutlet, KeyValuePipe } from '@angular/common';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NextSelectorComponent } from '../../../../json-to-from/next-selector/next-selector.component';

const NODE_TYPE = 'dynamicList';

@Component({
    selector: 'app-dynamic-list',
    templateUrl: './dynamic-list.component.html',
    styleUrls: ['./dynamic-list.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, ReactiveFormsModule, CMSComponent, DisplayTypeDialogComponent, NgTemplateOutlet, MatSelect, MatOption, MatSlideToggle, NextSelectorComponent, KeyValuePipe]
})
export class DynamicListComponent extends NodeBuilder implements OnInit, OnDestroy {

    menuParams: UntypedFormGroup = undefined;
    optionSeperatorList: string[] = ['. ', '- '];
    @Input() canEditFlows: boolean;

    canEdit: EditPermissions;
    validatiorFields = ['excludePattern', 'excludeValue', 'filterByPattern', 'filterByValue'];
    clearValidatiorsFields = ['excludeValue', 'excludePattern', 'filterByValue', 'filterByPattern'];
    metaDataObj: any = {};
    stepUniqueID: string = '';
    displayTypeModalData: DisplayTypeModalData = undefined;

    constructor(
        private activeFormService: ActiveFormService,
    ) {
        super(NODE_TYPE);
    }

    ngOnInit(): void {

    }

    initForm() {
        const {editMenuTextOnly, editFlows} = getEditTypes(this.canEdit);
        this.menuParams = new UntypedFormGroup({
            menuHeader: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }),
            groupTitle: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }),
            bodyText: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }, [required('Message To User')]),
            varName: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Variable Name')]),
            optionPattern: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('List Option Pattern')]),
            valuePattern: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            descriptionPattern: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            optionSeperator: new UntypedFormControl({
                value: '. ',
                disabled: !editFlows
            }),
            pagination: new UntypedFormControl({
                value: true,
                disabled: !editFlows
            }),
            nextOptionTitle: new UntypedFormControl({
                value: 'Next',
                disabled: !editFlows
            }),
            previousOptionTitle: new UntypedFormControl({
                value: 'Back',
                disabled: !editFlows
            }),
            filterByPattern: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            filterByValue: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            excludePattern: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            excludeValue: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            success: new UntypedFormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            error: new UntypedFormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            default: new UntypedFormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            displayType: new UntypedFormGroup({}),
            customListButton: new UntypedFormControl("")
        });
        this.activeFormService.setActiveForm(this.menuParams);
    }

    addSeparator(e, valueInput): void {
        e.stopPropagation();
        if (valueInput !== '') {
            this.optionSeperatorList.push(valueInput);
        }
    }

    get getStepData(): {} {
        const formData = this.menuParams.getRawValue();
        this.metaDataObj.nodeVersion = '1.2.0';
        if (this.stepUniqueID !== '') {
            this.metaDataObj = {
                ...this.metaDataObj,
                stepUniqueID: this.stepUniqueID
            }
        }

        let nodeData = {
            menu: this.getFormData(formData),
            type: 'menu',
            metadata: this.metaDataObj
        };
        return nodeData;
    }

    get isNodeValid(): boolean {
        this.validatiorFields.forEach((fieldName, index) => {
            if (this.menuParams.get(fieldName).value === '') {
                const clearValidationField = this.clearValidatiorsFields[index];
                this.menuParams.controls[clearValidationField].clearValidators();
                this.menuParams.get(clearValidationField).setErrors(null);
            }
        });
        this.menuParams.markAllAsTouched();
        return this.menuParams.valid;
    }

    set buildForm(stepData: any) {
        this.initForm();

        if (!isEmpty(stepData)) {
            const formData = this.stepToForm(stepData)
            this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
            this.menuParams.patchValue(formData);
            if (!this.optionSeperatorList.includes(stepData.menu.menuParams.optionSeperator)) {
                this.optionSeperatorList.push(stepData.menu.menuParams.optionSeperator);
            }
        }
        this.setDisplayType(stepData?.menu?.menuParams?.displayTypes, stepData?.menu?.menuParams?.customListButton);
        this.patchInitialResultsStepNumber();
    }

    private patchInitialResultsStepNumber() {
        if (this.menuParams.get('success').value === newStepValue) {
            this.menuParams.get('success').setValue('step' + this.getNextStep());
        }
        if (this.menuParams.get('error').value === newStepValue) {
            this.menuParams.get('error').setValue('step' + this.getNextStep());
        }
        if (this.menuParams.get('default').value === newStepValue) {
            this.menuParams.get('default').setValue('OPTIONAL');
        }
    }

    stepToForm(stepData) {
        const menu = stepData.menu;
        const stepMetaData = stepData.metadata;
        const filterBy = this.getPatternValue(menu.menuParams.filterBy);
        const exclude = this.getPatternValue(menu.menuParams.exclude);
        return {
            menuHeader: menu.menuHeader,
            groupTitle: menu.menuParams.groupTitle,
            bodyText: menu.bodyText,
            varName: menu.input.varName,
            optionPattern: menu.menuParams.optionPattern,
            valuePattern: menu.menuParams.valuePattern,
            descriptionPattern: menu.menuParams.descriptionPattern,
            optionSeperator: menu.menuParams.optionSeperator,
            pagination: menu.menuParams.pagination,
            nextOptionTitle: menu.menuParams.nextOptionTitle,
            previousOptionTitle: menu.menuParams.previousOptionTitle,
            displayTypes: menu.menuParams.displayTypes,
            filterByPattern: filterBy.pattern,
            filterByValue: filterBy.value,
            excludePattern: exclude.pattern,
            excludeValue: exclude.value,
            success: menu.results.success.next,
            error: menu.results.error.next,
            default: menu.results.default?.next ? menu.results.default.next : 'OPTIONAL'
        }
    }

    getPatternValue(objData) {
        const data = Object.keys(objData);
        return {
            pattern: data[0] ? data[0] : '',
            value: data[0] ? objData[data[0]] : ''
        }
    }

    getFormData(formData) {
        return {
            menuType: NODE_TYPE,
            menuHeader: formData.menuHeader,
            bodyText: formData.bodyText,
            results: {
                success: {
                    next: formData.success,
                },
                error: {
                    next: formData.error,
                },
                default: {
                    next: formData.default ? formData.default : 'OPTIONAL',
                },
            },
            menuParams: {
                groupTitle: formData.groupTitle,
                optionPattern: formData.optionPattern,
                valuePattern: formData.valuePattern,
                descriptionPattern: formData.descriptionPattern,
                optionSeperator: formData.optionSeperator,
                pagination: formData.pagination,
                nextOptionTitle: formData.nextOptionTitle,
                previousOptionTitle: formData.previousOptionTitle,
                displayTypes: formData.displayType,
                customListButton: formData.customListButton,
                filterBy: formData.filterByPattern && formData.filterByValue ? {
                    [formData.filterByPattern]: formData.filterByValue
                } : {},
                exclude: formData.excludePattern && formData.excludeValue ? {
                    [formData.excludePattern]: formData.excludeValue
                } : {}
            },
            input: {
                isPrivateVariable: false,
                varName: formData.varName
            },
        }
    }

    getformValue(form: UntypedFormGroup, controlName: string) {
        return form.get(controlName).value;
    }

    ngOnDestroy(): void {
        this.activeFormService.clearActiveForm();
    }


    /**
     *
     * @param nodeDisplayType The current displayType config -> 'number' || {8, 'NUMBER', 13: 'LIST'}
     * @param nodeCustomListButton The current customListButton -> 'string' || Options
     * @private Gets all the channels that are configured for this client and sets the new displayType structure
     */
    private setDisplayType(nodeDisplayType: {}, nodeCustomListButton: string) {
        const displayType = nodeDisplayType ? nodeDisplayType : {};
        const customListButton = nodeCustomListButton ? nodeCustomListButton : "";
        this.displayTypeModalData = {
            isDirty: false,
            displayType,
            customListButton
        }
        if (!isEmpty(displayType)) {
            this.patchDisplayType(this.displayTypeModalData);
        }
    }

    displayChange(event) {
        this.patchDisplayType(event);
    }

    private patchDisplayType(event) {
        const displayTypeFormGroup: UntypedFormGroup = this.menuParams.get('displayType') as UntypedFormGroup
        for (const channelId in event?.displayType) {
            const displayTypeValue = event?.displayType[channelId]
            if (displayTypeFormGroup.get(channelId)) {
                displayTypeFormGroup.get(channelId).setValue(displayTypeValue)
            } else {
                displayTypeFormGroup.addControl(channelId, new UntypedFormControl(displayTypeValue));
            }
        }
        if(event.isDirty) {
            displayTypeFormGroup.markAsDirty();
        }
        this.menuParams.get('customListButton').setValue(event?.customListButton);
    }

}
